import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useApiClient } from '../../api/apiClient';
import { useClient } from '../../context/ClientContext';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AwsOnboardingPanel from '../../components/aws/AwsOnboardingPanel';
import { isDemoPolishEnabled } from '../../utils/mspPolish';

const iS = {
  width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
  padding: '9px 11px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none',
  boxSizing: 'border-box',
};

const TIERS = [
  { id: 0, name: 'Baseline', desc: 'Network scanning, vulnerability management' },
  { id: 1, name: 'Baseline Plus', desc: 'Adds cloud security, OSINT, compliance' },
  { id: 2, name: 'Supreme', desc: 'Full platform: AD, DAST, attack paths, threat intel' },
];

const OBLIGATIONS = [
  { id: 'cmmc',      sends: ['cmmc1', 'cmmc2'], name: 'CMMC',         desc: 'Department of Defense contractor' },
  { id: 'hipaa',     sends: ['hipaa'],          name: 'HIPAA',        desc: 'Healthcare organization' },
  { id: 'pci',       sends: ['pci'],            name: 'PCI DSS',      desc: 'Processes payment cards' },
  { id: 'soc2',      sends: ['soc2'],           name: 'SOC 2',        desc: 'SaaS or service provider' },
  { id: 'nist',      sends: ['nist'],           name: 'NIST 800-53',  desc: 'Federal agency or contractor' },
  { id: 'cis',       sends: ['cis'],            name: 'CIS Controls', desc: 'General security baseline' },
  { id: 'sec-cyber', sends: ['sec-cyber'],      name: 'SEC Cyber',    desc: 'SEC-regulated entity' },
  { id: 'iso27001',  sends: ['iso27001'],       name: 'ISO 27001',    desc: 'International security standard' },
];

const MODULES = [
  { id: 'vuln',  name: 'Vulnerability Scanning', desc: 'Nessus vulnerability assessment and import pipeline', tier: 0 },
  { id: 'osint', name: 'OSINT & Breach Intelligence', desc: 'Shodan, DNS, certificates, M365 exposure, breached credentials', tier: 1 },
  { id: 'azure', name: 'M365 / Azure Security', desc: 'M365 baseline, Azure CSPM, SharePoint and Blob sensitive data discovery', tier: 1 },
  { id: 'aws',   name: 'AWS Security', desc: 'AWS CSPM and S3 sensitive data discovery', tier: 1 },
  { id: 'dast',  name: 'DAST Web Scanning', desc: 'Dynamic application security testing with managed runners', tier: 2 },
  { id: 'ad',    name: 'Active Directory Security', desc: 'AD inventory, permissions, certificate risk, attack paths, password audit', tier: 2 },
  { id: 'sdd',   name: 'Sensitive Data Discovery', desc: 'Cloud, SharePoint, file share, and storage discovery for PII and secrets', tier: 2 },
];

const STEPS = ['Company Info', 'Domain & Tier', 'OSINT Scope', 'Compliance', 'Cloud Access', 'Scan Scope', 'Modules', 'Review & Confirm'];
const AZURE_APP_CLIENT_ID = import.meta.env.VITE_AZURE_APP_CLIENT_ID || 'a8ed6b7d-621b-4362-abaf-d48240a7e36c';
const AZURE_CONSENT_REDIRECT_URI = import.meta.env.VITE_AZURE_CONSENT_REDIRECT_URI || 'https://portal.office.com';
const LICENSE_KEY_RE = /^SSS-[A-Z0-9]{3}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

const SDD_SOURCES = [
  { id: 's3', name: 'AWS S3' },
  { id: 'file-shares', name: 'Local File Shares' },
  { id: 'sharepoint', name: 'M365 SharePoint' },
  { id: 'azure-blob', name: 'Azure Blob' },
  { id: 'azure-sql', name: 'Azure SQL' },
];

function parseLines(value) {
  return String(value || '')
    .split(/[\n,;]+/)
    .map(v => v.trim())
    .filter(Boolean)
    .filter((v, i, arr) => arr.findIndex(x => x.toLowerCase() === v.toLowerCase()) === i);
}

function inferSensitiveSource(path) {
  const value = String(path || '').trim().toLowerCase();
  if (value.startsWith('s3://')) return 's3';
  if (value.includes('.blob.core.windows.net')) return 'azure-blob';
  if (value.startsWith('\\\\') || value.startsWith('//')) return 'file-share';
  if (value.includes('sharepoint.com') || value.startsWith('/sites/')) return 'sharepoint';
  if (value.includes('sql') || value.includes('].[')) return 'azure-sql';
  return 'any';
}

function parseDeclaredSensitiveLocations(value) {
  return String(value || '')
    .split(/\n+/)
    .map(v => v.trim())
    .filter(Boolean)
    .map(line => {
      const parts = line.split('|').map(v => v.trim()).filter(Boolean);
      if (parts.length >= 3) {
        return {
          label: parts[0],
          sourceType: parts[1],
          path: parts[2],
          criticality: parts[3] || 'high',
          category: parts[4] || 'client-declared',
        };
      }
      return {
        label: line,
        sourceType: inferSensitiveSource(line),
        path: line,
        criticality: 'high',
        category: 'client-declared',
      };
    })
    .filter((item, i, arr) => arr.findIndex(x => x.path.toLowerCase() === item.path.toLowerCase()) === i);
}

function parseTenantIds(primary, additional) {
  return [primary, ...String(additional || '').split(/[\s,;]+/)]
    .map(v => v.trim())
    .filter(Boolean)
    .filter((v, i, arr) => arr.findIndex(x => x.toLowerCase() === v.toLowerCase()) === i);
}

function countLabel(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function StepBar({ step }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
      {STEPS.map((s, i) => (
        <div key={s} style={{ flex: 1 }}>
          <div style={{ height: 3, borderRadius: 2, background: i < step ? C.grn : i === step ? C.sky : C.b1, transition: 'background 0.2s' }} />
          <div style={{ fontSize: 9, color: i <= step ? C.t1 : C.t3, fontFamily: FONTS.mono, marginTop: 4, textAlign: 'center' }}>
            {i + 1}. {s}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const api = useApiClient();
  const { setClientId, setClientName } = useClient();
  const [step, setStep] = useState(0);

  // Step 1 - Company Info
  const [name, setName] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [licenseError, setLicenseError] = useState('');

  // Step 2 - Domain & Tier
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [tier, setTier] = useState(0);

  // Step 3 - OSINT Scope
  const [alternateDomains, setAlternateDomains] = useState('');
  const [seedEmails, setSeedEmails] = useState('');
  const [publicIpRanges, setPublicIpRanges] = useState('');
  const [knownBrandsAssets, setKnownBrandsAssets] = useState('');

  // Step 4 - Compliance Obligations
  const [obligations, setObligations] = useState([]);
  const toggleObligation = (id) => setObligations(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const selectedFrameworkIds = obligations
    .map(id => OBLIGATIONS.find(o => o.id === id))
    .filter(Boolean)
    .flatMap(o => o.sends);

  // Step 5 - Cloud Access
  const [azTenantId, setAzTenantId] = useState('');
  const [azAdditionalTenantIds, setAzAdditionalTenantIds] = useState('');
  const [azSubscriptionScopes, setAzSubscriptionScopes] = useState('');
  const [azConsentConfirmed, setAzConsentConfirmed] = useState(false);
  const [azRbacBootstrap, setAzRbacBootstrap] = useState(null);
  const [azRbacLoading, setAzRbacLoading] = useState(false);
  const [azRbacError, setAzRbacError] = useState('');
  const [azRbacCopyStatus, setAzRbacCopyStatus] = useState('');

  const [azSkipped, setAzSkipped] = useState(false);
  const [azTesting, setAzTesting] = useState(false);
  const [azTestResult, setAzTestResult] = useState(null);
  const [azDiscovery, setAzDiscovery] = useState(null);

  const [awsAccountId, setAwsAccountId] = useState('');
  const [awsRegions, setAwsRegions] = useState('us-east-1');
  const [awsAccessKey, setAwsAccessKey] = useState('');
  const [awsSecretKey, setAwsSecretKey] = useState('');
  const [awsRoleArn, setAwsRoleArn] = useState('');
  const [awsPartition, setAwsPartition] = useState('aws');
  const [awsSkipped, setAwsSkipped] = useState(false);
  const [awsTesting, setAwsTesting] = useState(false);
  const [awsTestResult, setAwsTestResult] = useState(null);
  const [awsSetupClientId, setAwsSetupClientId] = useState(null);
  const [awsSetupPreparing, setAwsSetupPreparing] = useState(false);
  const [awsSetupError, setAwsSetupError] = useState('');
  const [awsSetupReady, setAwsSetupReady] = useState(false);

  // Step 6 - Scan Scope
  const [dastWebTargets, setDastWebTargets] = useState('');
  const [dastAuthRequirements, setDastAuthRequirements] = useState('');
  const [dastRunnerLocation, setDastRunnerLocation] = useState('');
  const [dastAllowedScanWindows, setDastAllowedScanWindows] = useState('');

  const [adDomainFqdn, setAdDomainFqdn] = useState('');
  const [adDcHostsIps, setAdDcHostsIps] = useState('');
  const [adServiceAccountUpn, setAdServiceAccountUpn] = useState('');
  const [adServiceAccountPassword, setAdServiceAccountPassword] = useState('');
  const [adRequiredRights, setAdRequiredRights] = useState('Directory read\nDomain computer read\nCertificate template read\nSession enumeration where approved');
  const [adLdapsStatus, setAdLdapsStatus] = useState('unknown');

  const [sddEnabledSources, setSddEnabledSources] = useState(['s3', 'file-shares', 'sharepoint', 'azure-blob']);
  const [sddS3Buckets, setSddS3Buckets] = useState('');
  const [sddFileSharePaths, setSddFileSharePaths] = useState('');
  const [sddSharePointScopes, setSddSharePointScopes] = useState('');
  const [sddAzureBlobScopes, setSddAzureBlobScopes] = useState('');
  const [sddAzureSqlConnections, setSddAzureSqlConnections] = useState('');
  const [sddDeclaredSensitiveLocations, setSddDeclaredSensitiveLocations] = useState('');

  const [remoteHostList, setRemoteHostList] = useState('');
  const [remoteHostProtocol, setRemoteHostProtocol] = useState('WinRM');
  const [remoteHostCredentialSource, setRemoteHostCredentialSource] = useState('');
  const [remoteHostPorts, setRemoteHostPorts] = useState('5986');
  const [remoteHostMaintenanceWindows, setRemoteHostMaintenanceWindows] = useState('');

  // Step 7 - Module Selection
  const [selectedModules, setSelectedModules] = useState([]);
  const toggleModule = (id) => setSelectedModules(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  // Launch state
  const [launching, setLaunching] = useState(false);
  const [launchStep, setLaunchStep] = useState('');
  const [launchError, setLaunchError] = useState('');
  const [launchSuccess, setLaunchSuccess] = useState(false);
  const [newClientId, setNewClientId] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stepWarnings, setStepWarnings] = useState([]);
  const [scanStatus, setScanStatus] = useState('');

  // A launch error describes the submitted inputs, not a later edited scope.
  useEffect(() => {
    setLaunchError('');
  }, [selectedModules, tier, name, licenseKey, primaryDomain, obligations,
    alternateDomains, seedEmails, publicIpRanges, knownBrandsAssets,
    azTenantId, azAdditionalTenantIds, azSubscriptionScopes, azConsentConfirmed, azSkipped,
    awsAccountId, awsRegions, awsAccessKey, awsSecretKey, awsRoleArn, awsPartition, awsSkipped,
    dastWebTargets, dastAuthRequirements, dastRunnerLocation, dastAllowedScanWindows,
    adDomainFqdn, adDcHostsIps, adServiceAccountUpn, adServiceAccountPassword, adRequiredRights, adLdapsStatus,
    sddEnabledSources, sddS3Buckets, sddFileSharePaths, sddSharePointScopes, sddAzureBlobScopes,
    sddAzureSqlConnections, sddDeclaredSensitiveLocations,
    remoteHostList, remoteHostProtocol, remoteHostCredentialSource, remoteHostPorts, remoteHostMaintenanceWindows]);

  const markComplete = (label) => setCompletedSteps(prev => [...prev, label]);
  const markWarning = (label, msg) => setStepWarnings(prev => [...prev, { label, msg }]);

  const azureConsentUrl = (tenantId = azTenantId) => {
    const tenant = (tenantId || 'common').trim();
    const params = new URLSearchParams({ client_id: AZURE_APP_CLIENT_ID, redirect_uri: AZURE_CONSENT_REDIRECT_URI });
    return `https://login.microsoftonline.com/${encodeURIComponent(tenant)}/adminconsent?${params.toString()}`;
  };

  const azureTenantIds = parseTenantIds(azTenantId, azAdditionalTenantIds);
  const azureSubscriptionScopes = parseLines(azSubscriptionScopes);
  const normalizeAzureRbacScope = (scope) => {
    const trimmed = (scope || '').trim();
    if (!trimmed) return '';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) return `/subscriptions/${trimmed}`;
    return trimmed;
  };
  const azureRbacTargetScopes = azureSubscriptionScopes.map(normalizeAzureRbacScope).filter(Boolean);
  const azureTenantKey = azureTenantIds.join('|');
  const azureRbacScopeKey = azureRbacTargetScopes.join('|');

  useEffect(() => {
    let active = true;
    if (azSkipped || azureTenantIds.length === 0) {
      setAzRbacBootstrap(null);
      setAzRbacLoading(false);
      setAzRbacError('');
      return () => { active = false; };
    }

    setAzRbacLoading(true);
    setAzRbacError('');
    api.post('/azure/rbac-bootstrap', { tenantIds: azureTenantIds, subscriptionScopes: azureRbacTargetScopes })
      .then(result => {
        if (!active) return;
        setAzRbacBootstrap(result);
        setAzRbacError('');
      })
      .catch(err => {
        if (!active) return;
        setAzRbacBootstrap(null);
        setAzRbacError(err?.message || 'Unable to generate Azure RBAC bootstrap artifacts.');
      })
      .finally(() => {
        if (active) setAzRbacLoading(false);
      });

    return () => { active = false; };
  }, [api, azSkipped, azureTenantKey, azureRbacScopeKey]);

  const azureRbacCliScript = () => {
    const artifacts = azRbacBootstrap?.azureCli;
    if (!artifacts) return '';
    return azureRbacTargetScopes.length > 0
      ? artifacts.configuredSubscriptionScopes || artifacts.autoEnumerateSubscriptions || ''
      : artifacts.autoEnumerateSubscriptions || artifacts.configuredSubscriptionScopes || '';
  };
  const azureRbacArmTemplate = () => azRbacBootstrap?.arm?.template
    ? JSON.stringify(azRbacBootstrap.arm.template, null, 2)
    : '';
  const copyAzureRbacCli = async () => {
    try {
      const script = azureRbacCliScript();
      if (!script) throw new Error('RBAC bootstrap is not ready');
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(script);
      setAzRbacCopyStatus('Azure CLI bootstrap copied.');
    } catch {
      setAzRbacCopyStatus('Clipboard unavailable. Select and copy the command block.');
    }
    window.setTimeout(() => setAzRbacCopyStatus(''), 3500);
  };
  const downloadAzureRbacArmTemplate = () => {
    const template = azureRbacArmTemplate();
    if (!template) {
      setAzRbacCopyStatus('Azure RBAC template is not ready.');
      window.setTimeout(() => setAzRbacCopyStatus(''), 3500);
      return;
    }
    const blob = new Blob([template], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sentinel-command-azure-rbac.json';
    link.click();
    URL.revokeObjectURL(url);
  };
  const osintAlternateDomains = parseLines(alternateDomains);
  const osintSeedDomains = parseLines([primaryDomain, alternateDomains].filter(Boolean).join('\n'));
  const osintSeedEmails = parseLines(seedEmails);
  const osintPublicIpRanges = parseLines(publicIpRanges);
  const osintKnownAssets = parseLines(knownBrandsAssets);
  const awsRegionSet = parseLines(awsRegions);
  const dastTargets = parseLines(dastWebTargets);
  const dastWindows = parseLines(dastAllowedScanWindows);
  const adDcList = parseLines(adDcHostsIps);
  const adRights = parseLines(adRequiredRights);
  const sddS3BucketList = parseLines(sddS3Buckets);
  const sddFileSharePathList = parseLines(sddFileSharePaths);
  const sddSharePointScopeList = parseLines(sddSharePointScopes);
  const sddAzureBlobScopeList = parseLines(sddAzureBlobScopes);
  const sddAzureSqlConnectionList = parseLines(sddAzureSqlConnections);
  const sddDeclaredSensitiveLocationList = parseDeclaredSensitiveLocations(sddDeclaredSensitiveLocations);
  const remoteHosts = parseLines(remoteHostList);
  const remotePorts = parseLines(remoteHostPorts);
  const remoteWindows = parseLines(remoteHostMaintenanceWindows);
  const hasDastScope = dastTargets.length > 0 || !!dastAuthRequirements || !!dastRunnerLocation || dastWindows.length > 0;
  const hasAdScope = !!adDomainFqdn || adDcList.length > 0 || !!adServiceAccountUpn || !!adServiceAccountPassword || adLdapsStatus !== 'unknown';
  const hasSddScope = sddEnabledSources.length > 0 || sddS3BucketList.length > 0 || sddFileSharePathList.length > 0 || sddSharePointScopeList.length > 0 || sddAzureBlobScopeList.length > 0 || sddAzureSqlConnectionList.length > 0 || sddDeclaredSensitiveLocationList.length > 0;
  const hasRemoteHostScope = remoteHosts.length > 0 || !!remoteHostCredentialSource || remoteWindows.length > 0 || (remotePorts.length > 0 && remotePorts.join(',') !== '5986');

  const selectedServiceModuleIds = () => {
    const selected = new Set(selectedModules);
    const moduleIds = new Set();
    const addIfSelected = (serviceId) => {
      if (selected.has(serviceId)) moduleIds.add(serviceId);
    };

    addIfSelected('vuln');
    addIfSelected('osint');
    addIfSelected('azure');
    addIfSelected('aws');
    addIfSelected('dast');
    addIfSelected('ad');

    if (selected.has('sdd')) {
      const wantsM365OrAzure =
        selected.has('azure') &&
        (sddEnabledSources.includes('sharepoint') ||
          sddEnabledSources.includes('azure-blob') ||
          sddEnabledSources.includes('azure-sql') ||
          sddSharePointScopeList.length > 0 ||
          sddAzureBlobScopeList.length > 0 ||
          sddAzureSqlConnectionList.length > 0);
      const wantsAws =
        selected.has('aws') &&
        (sddEnabledSources.includes('s3') || sddS3BucketList.length > 0);
      const wantsFileShares =
        sddEnabledSources.includes('file-shares') ||
        sddFileSharePathList.length > 0;

      if (wantsM365OrAzure) moduleIds.add('sdd-cloud');
      if (wantsAws) moduleIds.add('sdd-aws');
      if (wantsFileShares) moduleIds.add('sdd-fileshare');
    }

    return [...moduleIds];
  };

  const toggleSddSource = (id) => setSddEnabledSources(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const validateLicense = (v) => {
    const next = String(v || '').trim().toUpperCase();
    setLicenseKey(next);
    if (next && !LICENSE_KEY_RE.test(next)) {
      setLicenseError('Format: SSS-XXX-XXXX-XXXX-XXXX');
    } else {
      setLicenseError('');
    }
  };

  const loadDemoSample = () => {
    setName('Northwind Health Partners');
    validateLicense('');
    setPrimaryDomain('northwindhealth.example');
    setTier(2);
    setAlternateDomains('portal.northwindhealth.example\nvpn.northwindhealth.example');
    setSeedEmails('it-admin@northwindhealth.example\nsecurity@northwindhealth.example');
    setPublicIpRanges('203.0.113.42/32\n198.51.100.0/29');
    setKnownBrandsAssets('Northwind Health\nNorthwind Patient Portal');
    setObligations(['hipaa', 'soc2', 'cis']);
    setAzSkipped(true);
    setAwsSkipped(true);
    setDastWebTargets('https://portal.northwindhealth.example\nhttps://api.northwindhealth.example');
    setDastAuthRequirements('Unauthenticated baseline first\nAuthenticated scan requires a test user');
    setDastRunnerLocation('Managed cloud runner with allowlisted source IPs');
    setDastAllowedScanWindows('Fri 20:00-23:00 CT\nSat 09:00-12:00 CT');
    setAdDomainFqdn('northwind.local');
    setAdDcHostsIps('DC01 10.60.0.10\nDC02 10.60.0.11');
    setAdServiceAccountUpn('svc-sentinel-scan@northwind.local');
    setAdRequiredRights('Directory read\nDomain computer read\nCertificate template read\nSession enumeration where approved');
    setAdLdapsStatus('confirmed');
    setSddEnabledSources(['file-shares', 'sharepoint', 'azure-blob']);
    setSddFileSharePaths('\\\\FS01\\Finance\n\\\\FS01\\HR');
    setSddSharePointScopes('/sites/Finance\n/sites/HR\n/sites/Clients');
    setSddAzureBlobScopes('northwindrecords/patient-exports\nnorthwindarchive/legal');
    setSddDeclaredSensitiveLocations('Finance Share | file-share | \\\\FS01\\Finance | critical | financial\nHR SharePoint | sharepoint | /sites/HR | high | employee-pii');
    setRemoteHostList('FS01 10.60.12.20\nSQL01 10.60.20.15');
    setRemoteHostCredentialSource('RMM vaulted credential approved for read-only inventory');
    setRemoteHostMaintenanceWindows('Mon-Fri 20:00-23:00 CT');
    setSelectedModules(['vuln', 'osint', 'azure', 'aws', 'dast', 'ad', 'sdd']);
  };

  const testAzure = async () => {
    setAzTesting(true);
    setAzTestResult(null);
    try {
      const tenantIds = parseTenantIds(azTenantId, azAdditionalTenantIds);
      const missingConsent = tenantIds.length > 0 && !azConsentConfirmed;
      setAzTestResult({
        ok: tenantIds.length > 0 && !missingConsent,
        msg: tenantIds.length > 0
          ? missingConsent
            ? `${countLabel(tenantIds.length, 'tenant')} listed. Open the consent link so Microsoft 365 and Azure ARM access can be established.`
            : `${countLabel(tenantIds.length, 'tenant')} recorded. Sentinel Command will auto-enumerate Azure subscriptions visible to the app; zero visible subscriptions is informational.`
          : 'Add at least one tenant ID or use tenant discovery.',
      });
    } catch (err) {
      setAzTestResult({ ok: false, msg: err.message });
    } finally {
      setAzTesting(false);
    }
  };

  const testAws = async () => {
    setAwsTesting(true);
    setAwsTestResult(null);
    try {
      const hasRoleOutputs = !!awsRoleArn;
      const hasAccessKeyFallback = !!awsAccountId && !!awsAccessKey && !!awsSecretKey;
      const hasPartialFallback = !!awsAccessKey || !!awsSecretKey;
      setAwsTestResult({
        ok: awsRegionSet.length > 0 && (hasRoleOutputs || hasAccessKeyFallback || (!awsAccountId && !hasPartialFallback)),
        msg: awsRegionSet.length === 0
            ? 'Add at least one AWS region.'
            : hasRoleOutputs
              ? 'AWS role outputs captured. Verify AWS access before finishing onboarding.'
              : hasAccessKeyFallback
                ? 'AWS access-key fallback captured. Role-based onboarding is preferred.'
                  : hasPartialFallback
                    ? 'Access-key fallback requires account ID, access key ID, and secret access key.'
                  : awsSetupClientId
                    ? 'AWS setup link is ready. Launch or copy it, then enter the AWS account ID and verify access.'
                    : 'Create the AWS setup link, send or launch it for the AWS admin, then verify access.',
      });
    } catch (err) {
      setAwsTestResult({ ok: false, msg: err.message });
    } finally {
      setAwsTesting(false);
    }
  };

  // Auto-select modules based on tier
  const initModulesForTier = () => {
    const eligible = MODULES.filter(m => m.tier <= tier).map(m => m.id);
    setSelectedModules(eligible);
    setStep(6);
  };

  const discoverAzureTenant = async () => {
    if (!primaryDomain) return;
    setAzDiscovery({ state: 'checking', msg: 'Checking primary domain...' });
    try {
      const result = await api.get(`/clients/discover-m365-tenant?domain=${encodeURIComponent(primaryDomain)}`);
      if (result?.tenantId) {
        setAzTenantId(prev => prev || result.tenantId);
        setAzDiscovery({ state: 'found', msg: `Tenant discovered: ${result.tenantId}` });
      } else {
        setAzDiscovery({ state: 'empty', msg: 'No Microsoft tenant was discovered for this domain.' });
      }
    } catch (err) {
      setAzDiscovery({ state: 'error', msg: err.message || 'Tenant discovery failed.' });
    }
  };

  const createClientForOnboarding = async () => {
    try {
      return await api.post('/clients', {
        name,
        tier,
        licenseKey: licenseKey || undefined,
        primaryDomain: primaryDomain || undefined,
        complianceFrameworks: selectedFrameworkIds,
      });
    } catch (err) {
      const status = err?.response?.status || err?.status;
      const body = err?.response?.data || err?.data || {};
      const code = body?.error?.code || '';
      if (status === 409 || code === 'DUPLICATE_DOMAIN') {
        throw new Error(`Domain "${primaryDomain}" is already registered to another client. Use a different domain or contact support.`);
      }
      if (status === 400) {
        const msg = body?.error?.message || body?.message || 'Validation failed';
        throw new Error(`Validation error: ${msg}`);
      }
      throw err;
    }
  };

  const prepareAwsSetupClient = async () => {
    if (awsSetupClientId) return awsSetupClientId;

    setAwsSetupPreparing(true);
    setAwsSetupError('');
    setAwsTestResult(null);
    try {
      if (!name.trim()) throw new Error('Enter the client name before creating the AWS setup link.');
      if (!primaryDomain.trim()) throw new Error('Enter the primary domain before creating the AWS setup link.');
      if (licenseError) throw new Error(licenseError);

      const client = await createClientForOnboarding();
      const cid = client.clientId || client.id;
      setAwsSetupClientId(cid);
      setNewClientId(cid);
      setAwsTestResult({ ok: true, msg: 'AWS setup link ready. Launch or copy the link, then verify after CloudFormation completes.' });
      return cid;
    } catch (err) {
      const msg = err?.message || 'AWS setup link could not be created.';
      setAwsSetupError(msg);
      setAwsTestResult({ ok: false, msg });
      return null;
    } finally {
      setAwsSetupPreparing(false);
    }
  };

  const handleLaunch = async () => {
    setLaunching(true);
    setLaunchError('');
    setCompletedSteps([]);
    setStepWarnings([]);
    setScanStatus('');
    let awsRoleOutputsSaved = false;
    let awsFallbackSaved = false;

    try {
      const moduleIds = selectedServiceModuleIds();
      if (moduleIds.length === 0) {
        throw new Error('Select at least one scanning service with a configured source before launching.');
      }

      setLaunchStep(awsSetupClientId ? 'Using prepared client...' : 'Creating client...');
      let cid = awsSetupClientId;
      if (!cid) {
        const client = await createClientForOnboarding();
        cid = client.clientId || client.id;
        markComplete('Create client');
      } else {
        markComplete('Use prepared client');
      }
      setNewClientId(cid);

      if (selectedModules.length > 0) {
        setLaunchStep('Saving selected services...');
        try {
          for (const moduleId of moduleIds) {
            await api.put(`/clients/${cid}/modules/${encodeURIComponent(moduleId)}`, { enabled: true });
          }
          markComplete('Save selected services');
        } catch (err) {
          throw new Error(`Save selected services failed: ${err.message || 'unknown error'}`);
        }
      }

      if (selectedFrameworkIds.length > 0) {
        setLaunchStep('Saving compliance obligations...');
        try {
          await api.put(`/clients/${cid}/compliance-frameworks`, { frameworks: selectedFrameworkIds });
          markComplete('Save compliance obligations');
        } catch (err) { markWarning('Save compliance obligations', err.message); }
      }

      if (osintSeedDomains.length > 0 || osintSeedEmails.length > 0 || osintPublicIpRanges.length > 0 || osintKnownAssets.length > 0) {
        setLaunchStep('Saving OSINT scope...');
        try {
          await api.post(`/clients/${cid}/environments`, {
            environmentType: 'Osint',
            isConfigured: true,
            configJson: JSON.stringify({
              primaryDomain: primaryDomain || null,
              seedDomains: osintSeedDomains,
              alternateDomains: osintAlternateDomains,
              seedEmails: osintSeedEmails,
              publicIpRanges: osintPublicIpRanges,
              seedIpRanges: osintPublicIpRanges,
              knownAssets: osintKnownAssets,
              knownBrandsAssets: osintKnownAssets,
            }),
          });
          markComplete('Save OSINT scope');
        } catch (err) { markWarning('Save OSINT scope', err.message); }
      }

      const azureTenantIds = parseTenantIds(azTenantId, azAdditionalTenantIds);
      if (!azSkipped && azureTenantIds.length > 0) {
        setLaunchStep('Saving Azure tenant configuration...');
        try {
          await api.post(`/clients/${cid}/secrets`, { key: 'azure-tenant-id', value: azureTenantIds[0] });
          await api.post(`/clients/${cid}/secrets`, { key: 'azure-tenant-ids', value: JSON.stringify(azureTenantIds) });
          markComplete('Save Azure tenant configuration');
        } catch (err) { markWarning('Save Azure tenant configuration', err.message); }
      }

      if (!awsSkipped) {
        setLaunchStep('Preparing AWS setup link...');
        try {
          const awsSetup = await api.get(`/clients/${cid}/aws/onboarding-link`);
          if (awsSetup?.readiness?.scanReady || awsSetup?.readiness?.roleConfigured) {
            awsRoleOutputsSaved = true;
            markComplete(awsSetup?.readiness?.scanReady ? 'AWS role scan-ready' : 'AWS role outputs present');
          } else {
            markComplete('Prepare AWS setup link');
          }
        } catch (err) { markWarning('Prepare AWS setup link', err.message); }
      }

      if (!awsSkipped && awsRoleArn) {
        setLaunchStep('Saving AWS role outputs...');
        try {
          await api.post(`/clients/${cid}/aws/stack-outputs`, {
            accountId: awsAccountId || null,
            roleArn: awsRoleArn,
            partition: awsPartition,
            regions: awsRegionSet,
          });
          awsRoleOutputsSaved = true;
          markComplete('Save AWS role outputs');
        } catch (err) { markWarning('Save AWS role outputs', err.message); }
      }

      if (!awsSkipped && (awsAccessKey || awsSecretKey)) {
        setLaunchStep('Saving AWS access-key fallback...');
        try {
          if (!awsAccountId || !awsAccessKey || !awsSecretKey) {
            throw new Error('Account ID, access key ID, and secret access key are required for fallback access-key setup.');
          }

          await api.post(`/clients/${cid}/secrets`, { key: 'aws/account-id', value: awsAccountId });
          await api.post(`/clients/${cid}/secrets`, { key: 'aws/access-key-id', value: awsAccessKey });
          await api.post(`/clients/${cid}/secrets`, { key: 'aws/secret-access-key', value: awsSecretKey });
          awsFallbackSaved = true;
          markComplete('Save AWS access-key fallback');
        } catch (err) { markWarning('Save AWS access-key fallback', err.message); }
      }

      if (!awsSkipped && !awsRoleOutputsSaved && !awsFallbackSaved) {
        markWarning('AWS setup', 'Use the AWS setup link after onboarding, enter the AWS account ID, and verify access.');
      }

      if (hasAdScope) {
        setLaunchStep('Saving AD connection requirements...');
        try {
          if (adServiceAccountUpn) {
            await api.post(`/clients/${cid}/secrets`, { key: 'ad/service-account-upn', value: adServiceAccountUpn });
            await api.post(`/clients/${cid}/secrets`, { key: 'ad/username', value: adServiceAccountUpn });
          }
          if (adServiceAccountPassword) {
            await api.post(`/clients/${cid}/secrets`, { key: 'ad/service-account-password', value: adServiceAccountPassword });
            await api.post(`/clients/${cid}/secrets`, { key: 'ad/password', value: adServiceAccountPassword });
          }
          if (adDomainFqdn) await api.post(`/clients/${cid}/secrets`, { key: 'ad/domain-fqdn', value: adDomainFqdn });
          if (adDcList[0]) await api.post(`/clients/${cid}/secrets`, { key: 'ad/domain-controller', value: adDcList[0] });
          markComplete('Save AD connection requirements');
        } catch (err) { markWarning('Save AD connection requirements', err.message); }
      }

      if (sddS3BucketList.length > 0 || sddFileSharePathList.length > 0 || sddAzureSqlConnectionList.length > 0 || sddDeclaredSensitiveLocationList.length > 0) {
        setLaunchStep('Saving sensitive data discovery scope...');
        try {
          if (sddS3BucketList.length > 0) await api.post(`/clients/${cid}/secrets`, { key: 'sdd/s3-bucket-names', value: JSON.stringify(sddS3BucketList) });
          if (sddFileSharePathList.length > 0) await api.post(`/clients/${cid}/secrets`, { key: 'sdd/fileshare-paths', value: JSON.stringify(sddFileSharePathList) });
          if (sddAzureSqlConnectionList.length > 0) await api.post(`/clients/${cid}/secrets`, { key: 'sdd/azure-sql-connections', value: JSON.stringify(sddAzureSqlConnectionList) });
          if (sddDeclaredSensitiveLocationList.length > 0) await api.post(`/clients/${cid}/secrets`, { key: 'sdd/declared-sensitive-locations', value: JSON.stringify(sddDeclaredSensitiveLocationList) });
          markComplete('Save SDD scope');
        } catch (err) { markWarning('Save SDD scope', err.message); }
      }

      if (!azSkipped && azureTenantIds.length > 0) {
        setLaunchStep('Creating Azure environment...');
        try {
          await api.post(`/clients/${cid}/environments`, {
            environmentType: 'AzureEntra',
            isConfigured: true,
            configJson: JSON.stringify({
              tenantId: azureTenantIds[0],
              tenantIds: azureTenantIds,
              additionalTenantIds: azureTenantIds.slice(1),
              authMode: 'platform-multitenant-app',
              credentialSource: 'client-secrets',
              consentStatus: azConsentConfirmed ? 'confirmed' : 'pending',
              consentConfirmed: azConsentConfirmed,
              consentUrl: azureConsentUrl(azureTenantIds[0]),
              subscriptionScopes: azureSubscriptionScopes,
              subscriptionIds: azureSubscriptionScopes,
              subscriptionDiscoveryMode: azureSubscriptionScopes.length > 0 ? 'allow-list' : 'auto',
              autoEnumerateSubscriptions: azureSubscriptionScopes.length === 0,
              roleAssignmentValidationMode: 'live-arm',
              requiredArmScope: 'https://management.azure.com/.default',
              requiredAzureRoleAssignments: ['Reader', 'Security Reader'],
              armAccessModel: 'service-principal-auto-discovery-rbac',
              authorizationScope: azureTenantIds[0] ? `/providers/Microsoft.Management/managementGroups/${azureTenantIds[0]}` : null,
              sddEnabledSources,
              sddSharePointScopes: sddSharePointScopeList,
              sddAzureBlobScopes: sddAzureBlobScopeList,
              sddAzureSqlConnectionCount: sddAzureSqlConnectionList.length,
              sddDeclaredSensitiveLocationCount: sddDeclaredSensitiveLocationList.length,
            }),
          });
          markComplete('Create Azure environment');
        } catch (err) { markWarning('Create Azure environment', err.message); }
      }

      if (!awsSkipped && awsFallbackSaved && awsAccountId) {
        setLaunchStep('Creating AWS fallback environment...');
        try {
          await api.post(`/clients/${cid}/environments`, {
            environmentType: 'Aws',
            isConfigured: true,
            configJson: JSON.stringify({
              accountId: awsAccountId,
              regions: awsRegionSet,
              region: awsRegionSet[0] || 'us-east-1',
              partition: awsPartition,
              roleArn: null,
              authMode: 'access-key',
              credentialSource: 'client-secrets',
              onboardingStatus: 'access-key-fallback',
              sddEnabledSources,
              sddS3Buckets: sddS3BucketList,
              sddDeclaredSensitiveLocationCount: sddDeclaredSensitiveLocationList.length,
            }),
          });
          markComplete('Create AWS fallback environment');
        } catch (err) { markWarning('Create AWS fallback environment', err.message); }
      }

      if (hasDastScope) {
        setLaunchStep('Creating DAST web application scope...');
        try {
          await api.post(`/clients/${cid}/environments`, {
            environmentType: 'WebApplication',
            isConfigured: dastTargets.length > 0,
            configJson: JSON.stringify({
              webTargets: dastTargets,
              authRequirements: dastAuthRequirements,
              runnerLocation: dastRunnerLocation,
              allowedScanWindows: dastWindows,
              manuallyConfigured: true,
              lastUpdatedAt: new Date().toISOString(),
            }),
          });
          markComplete('Create DAST scope');
        } catch (err) { markWarning('Create DAST scope', err.message); }
      }

      if (hasRemoteHostScope || hasAdScope || sddFileSharePathList.length > 0) {
        setLaunchStep('Creating remote host and AD scope...');
        try {
          await api.post(`/clients/${cid}/environments`, {
            environmentType: 'RemoteHosts',
            isConfigured: hasRemoteHostScope || hasAdScope || sddFileSharePathList.length > 0,
            configJson: JSON.stringify({
              hosts: remoteHosts,
              protocol: remoteHostProtocol,
              credentialSource: remoteHostCredentialSource,
              ports: remotePorts,
              maintenanceWindows: remoteWindows,
              fileSharePaths: sddFileSharePathList,
              declaredSensitiveLocations: sddDeclaredSensitiveLocationList,
              activeDirectory: {
                domainFqdn: adDomainFqdn || null,
                dcHostsIps: adDcList,
                serviceAccountUpn: adServiceAccountUpn || null,
                requiredRights: adRights,
                ldapsConnectivityStatus: adLdapsStatus,
              },
            }),
          });
          markComplete('Create remote host and AD scope');
        } catch (err) { markWarning('Create remote host and AD scope', err.message); }
      }

      setLaunchStep('Triggering first scan...');
      try {
        const scanResult = await api.post(`/clients/${cid}/scans`, { modules: moduleIds });
        setScanStatus(scanResult?.status || 'Queued');
        markComplete('Trigger first scan');
      } catch (err) { markWarning('Trigger first scan', err.message); }

      setLaunchSuccess(true);
    } catch (err) {
      setLaunchError(err.message || 'An unexpected error occurred. Check your connection and try again.');
    } finally {
      setLaunching(false);
    }
  };

  // ---- Step Renderers ----

  const renderStep0 = () => (
    <Card style={{ padding: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1, marginBottom: 18 }}>Company Information</div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>CLIENT NAME *</div>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Acme Corporation" style={iS} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>LICENSE KEY (optional)</div>
        <input value={licenseKey} onChange={e => validateLicense(e.target.value.toUpperCase())} placeholder="SSS-XXX-XXXX-XXXX-XXXX" style={iS} />
        {licenseError && <div style={{ fontSize: 9, color: C.red, fontFamily: FONTS.mono, marginTop: 4 }}>{licenseError}</div>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {isDemoPolishEnabled() && <Button variant="sec" onClick={loadDemoSample} style={{ marginRight: 8 }}>Use Demo Sample</Button>}
        <Button variant="pri" disabled={!name.trim() || !!licenseError} onClick={() => setStep(1)}>Next {'\u2192'}</Button>
      </div>
    </Card>
  );

  const renderStep1 = () => (
    <Card style={{ padding: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1, marginBottom: 18 }}>Domain & Service Tier</div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>PRIMARY DOMAIN</div>
        <input value={primaryDomain} onChange={e => setPrimaryDomain(e.target.value)} placeholder="acmecorp.com" style={iS} />
        <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, marginTop: 3 }}>Used for OSINT scanning. Alternate domains, public IP ranges, seed emails, and brands are captured next.</div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 8 }}>SERVICE TIER *</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
          {TIERS.map(t => (
            <div key={t.id} onClick={() => setTier(t.id)} style={{
              padding: '14px 16px', borderRadius: 8, cursor: 'pointer',
              background: tier === t.id ? `${C.sky}10` : C.s2,
              border: `1px solid ${tier === t.id ? C.sky : C.b1}`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: tier === t.id ? C.sky : C.t1, fontFamily: FONTS.sans }}>{t.name}</div>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 4 }}>{t.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="sec" onClick={() => setStep(0)}>{'\u2190'} Back</Button>
        <Button variant="pri" onClick={async () => { await discoverAzureTenant(); setStep(2); }}>Next: OSINT Scope {'\u2192'}</Button>
      </div>
    </Card>
  );

  const renderStep2 = () => (
    <Card style={{ padding: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1, marginBottom: 6 }}>OSINT Scope</div>
      <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 18 }}>
        Seed the external reconnaissance modules with the public assets this client cares about.
      </div>

      <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8, background: `${C.sky}08`, border: `1px solid ${C.sky}22` }}>
        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>PRIMARY DOMAIN</div>
        <div style={{ fontSize: 13, color: C.t1, fontWeight: 700, fontFamily: FONTS.mono }}>{primaryDomain || '(not set)'}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>ALTERNATE DOMAINS</div>
          <textarea value={alternateDomains} onChange={e => setAlternateDomains(e.target.value)} placeholder={"example.net\nexample.org"} rows={5} style={{ ...iS, resize: 'vertical' }} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>SEED EMAILS</div>
          <textarea value={seedEmails} onChange={e => setSeedEmails(e.target.value)} placeholder={"security@example.com\nit@example.com"} rows={5} style={{ ...iS, resize: 'vertical' }} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>PUBLIC IP RANGES</div>
          <textarea value={publicIpRanges} onChange={e => setPublicIpRanges(e.target.value)} placeholder={"203.0.113.0/24\n198.51.100.10"} rows={5} style={{ ...iS, resize: 'vertical' }} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>KNOWN BRANDS / ASSETS</div>
          <textarea value={knownBrandsAssets} onChange={e => setKnownBrandsAssets(e.target.value)} placeholder={"Acme Portal\nAcme Payments\nlegacy.acme.com"} rows={5} style={{ ...iS, resize: 'vertical' }} />
        </div>
      </div>

      <div style={{ marginBottom: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[
          countLabel(osintSeedDomains.length, 'domain'),
          countLabel(osintSeedEmails.length, 'email'),
          countLabel(osintPublicIpRanges.length, 'IP range'),
          countLabel(osintKnownAssets.length, 'asset'),
        ].map(item => (
          <span key={item} style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, padding: '5px 8px', borderRadius: 999, border: `1px solid ${C.b1}`, background: C.s2 }}>
            {item}
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="sec" onClick={() => setStep(1)}>{'\u2190'} Back</Button>
        <Button variant="pri" onClick={() => setStep(3)}>Next {'\u2192'}</Button>
      </div>
    </Card>
  );

  const renderStep3 = () => (
    <Card style={{ padding: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1, marginBottom: 6 }}>Compliance Frameworks</div>
      <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 18 }}>Select every framework this client must comply with. Compliance scoring and gap reports are built against these selections.</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 18 }}>
        {OBLIGATIONS.map(o => {
          const checked = obligations.includes(o.id);
          return (
            <div key={o.id} onClick={() => toggleObligation(o.id)} style={{
              padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
              background: checked ? `${C.sky}10` : C.s2,
              border: `1px solid ${checked ? C.sky : C.b1}`,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 2,
                background: checked ? C.sky : 'transparent',
                border: `1.5px solid ${checked ? C.sky : C.t3}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 10, fontWeight: 900,
              }}>{checked ? '\u2713' : ''}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: checked ? C.sky : C.t1, fontFamily: FONTS.sans }}>{o.name}</div>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 3 }}>{o.desc}</div>
              </div>
            </div>
          );
        })}
      </div>

      {obligations.length === 0 && (
        <div style={{ marginBottom: 14, fontSize: 10, color: C.amb, fontFamily: FONTS.mono }}>
          Select at least one framework to continue.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="sec" onClick={() => setStep(2)}>{'\u2190'} Back</Button>
        <Button variant="pri" disabled={obligations.length === 0} onClick={() => setStep(4)}>Next {'\u2192'}</Button>
      </div>
    </Card>
  );

  const renderStep4 = () => (
    <Card style={{ padding: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1, marginBottom: 6 }}>Cloud Access</div>
      <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 18 }}>Connect M365/Azure with Microsoft admin consent. Sentinel Command auto-discovers Azure subscriptions visible to the app; no visible subscriptions is informational.</div>

      {/* Azure Section */}
      <div style={{ marginBottom: 20, padding: 16, borderRadius: 8, border: `1px solid ${C.b1}`, background: azSkipped ? `${C.s2}80` : C.s1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: azSkipped ? 0 : 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: azSkipped ? C.t3 : C.t1, fontFamily: FONTS.sans }}>Azure / M365</div>
          <button onClick={() => setAzSkipped(!azSkipped)} style={{ background: 'none', border: 'none', color: azSkipped ? C.sky : C.t3, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.mono }}>
            {azSkipped ? 'Enable' : 'Skip'}
          </button>
        </div>

        {!azSkipped && (
          <>
            {[
              { l: 'Primary Tenant ID', v: azTenantId, s: setAzTenantId, p: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
            ].map(f => (
              <div key={f.l} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{f.l}</div>
                <input type={f.t || 'text'} value={f.v} onChange={e => f.s(e.target.value)} placeholder={f.p} style={iS} />
              </div>
            ))}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Additional Tenant IDs</div>
              <textarea value={azAdditionalTenantIds} onChange={e => setAzAdditionalTenantIds(e.target.value)} placeholder={"Optional, one tenant ID per line. Add another tenant after each consent."} rows={3} style={{ ...iS, resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Advanced Subscription Allow-List</div>
              <textarea value={azSubscriptionScopes} onChange={e => setAzSubscriptionScopes(e.target.value)} placeholder={"/subscriptions/00000000-0000-0000-0000-000000000000\n00000000-0000-0000-0000-000000000001"} rows={3} style={{ ...iS, resize: 'vertical' }} />
              <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, marginTop: 3 }}>Leave blank for one-click auto-discovery. Add explicit subscription IDs only when the review must be restricted to a smaller allow-list than the app can see.</div>
            </div>
            <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8, background: C.s2, border: `1px solid ${C.b1}` }}>
              <div style={{ marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans }}>Admin consent link</div>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>
                    Global Admin consent records the Microsoft authorization. Sentinel Command uses ARM access with https://management.azure.com/.default and Azure RBAC visibility to discover subscriptions automatically.
                  </div>
                </div>
              </div>
              {azureTenantIds.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                  {azureTenantIds.map(tenant => (
                    <a
                      key={tenant}
                      href={azureConsentUrl(tenant)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        padding: '7px 9px',
                        borderRadius: 6,
                        background: C.s1,
                        border: `1px solid ${C.b1}`,
                        color: C.sky,
                        fontSize: 9,
                        fontFamily: FONTS.mono,
                        lineHeight: 1.45,
                        wordBreak: 'break-all',
                      }}
                    >
                      {azureConsentUrl(tenant)}
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginBottom: 8 }}>
                  Enter a tenant ID to generate the Microsoft admin consent link.
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginBottom: 8 }}>
                <input type="checkbox" checked={azConsentConfirmed} onChange={e => setAzConsentConfirmed(e.target.checked)} />
                Admin consent granted for the listed tenant(s)
              </label>
              <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5 }}>Reader and Security Reader are validated live from ARM subscription visibility and Microsoft.Security posture reads.</div>
            </div>
            {azureTenantIds.length > 0 && (
              <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8, background: C.s2, border: `1px solid ${C.b1}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 4 }}>Azure RBAC bootstrap</div>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5, marginBottom: 8 }}>
                  {azureRbacTargetScopes.length > 0
                    ? 'Uses the subscription allow-list above. Leave it blank when the app should auto-discover every subscription it can see.'
                    : 'Uses the server-generated bootstrap to enumerate enabled subscriptions visible to the signed-in Azure admin, then grants Sentinel Command Reader and Security Reader at each subscription.'}
                </div>
                <textarea readOnly value={azRbacLoading ? 'Generating server-side Azure RBAC bootstrap...' : azRbacError ? azRbacError : azureRbacCliScript()} rows={8} style={{ ...iS, fontSize: 8, minHeight: 164, resize: 'vertical', whiteSpace: 'pre', lineHeight: 1.45 }} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  <Button variant="sec" size="sm" disabled={azRbacLoading || !azRbacBootstrap} onClick={copyAzureRbacCli}>Copy Azure CLI</Button>
                  <Button variant="sec" size="sm" disabled={azRbacLoading || !azRbacBootstrap} onClick={downloadAzureRbacArmTemplate}>Download ARM Template</Button>
                </div>
                {azRbacCopyStatus && <div style={{ marginTop: 6, fontSize: 9, color: C.grn, fontFamily: FONTS.mono }}>{azRbacCopyStatus}</div>}
                <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, lineHeight: 1.5, marginTop: 8 }}>
                  Requires Owner, User Access Administrator, or Role Based Access Control Administrator at the target Azure scope. For inherited management group access, assign the same roles at the management group and leave the subscription allow-list blank so Sentinel Command uses live ARM auto-discovery.
                </div>
              </div>
            )}
            {azDiscovery?.msg && (
              <div style={{ marginBottom: 10, fontSize: 10, color: azDiscovery.state === 'found' ? C.grn : azDiscovery.state === 'error' ? C.red : C.t2, fontFamily: FONTS.mono }}>
                {azDiscovery.msg}
              </div>
            )}
            {azTestResult && (
              <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: azTestResult.ok ? `${C.grn}12` : C.redBg, border: `1px solid ${azTestResult.ok ? C.grn : C.red}33`, color: azTestResult.ok ? C.grn : C.red }}>
                {azTestResult.msg}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="sec" size="sm" disabled={!primaryDomain || azDiscovery?.state === 'checking'} onClick={discoverAzureTenant}>
                {azDiscovery?.state === 'checking' ? 'Checking...' : 'Detect Tenant'}
              </Button>
              <Button variant="sec" size="sm" disabled={azTesting || !azTenantId} onClick={testAzure}>
                {azTesting ? 'Checking...' : 'Check Microsoft Consent'}
              </Button>
            </div>
            <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, marginTop: 8 }}>
              After onboarding, Client Credentials can refresh Azure subscription discovery and verify live ARM visibility for the saved tenant IDs.
            </div>
          </>
        )}
      </div>

      {/* AWS Section */}
      <div style={{ marginBottom: 20, padding: 16, borderRadius: 8, border: `1px solid ${C.b1}`, background: awsSkipped ? `${C.s2}80` : C.s1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: awsSkipped ? 0 : 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: awsSkipped ? C.t3 : C.t1, fontFamily: FONTS.sans }}>AWS</div>
          <button onClick={() => setAwsSkipped(!awsSkipped)} style={{ background: 'none', border: 'none', color: awsSkipped ? C.sky : C.t3, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.mono }}>
            {awsSkipped ? 'Enable' : 'Skip'}
          </button>
        </div>

        {!awsSkipped && (
          <>
            <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8, background: C.s2, border: `1px solid ${C.b1}`, fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.55 }}>
              Create the AWS setup link here, open or copy it for the AWS admin, then verify access after CloudFormation completes.
            </div>
            {!awsSetupClientId ? (
              <div style={{ marginBottom: 12, padding: '12px 14px', borderRadius: 8, background: `${C.sky}08`, border: `1px solid ${C.sky}22` }}>
                <div style={{ fontSize: 11, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans, marginBottom: 5 }}>AWS setup link</div>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5, marginBottom: 10 }}>
                  This creates the setup link now and fills in the required role values for AWS. Final confirmation will reuse the same client.
                </div>
                {awsSetupError && (
                  <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 6, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, fontSize: 10, fontFamily: FONTS.mono }}>
                    {awsSetupError}
                  </div>
                )}
                <Button
                  variant="pri"
                  size="sm"
                  disabled={awsSetupPreparing || !name.trim() || !primaryDomain.trim() || !!licenseError}
                  onClick={prepareAwsSetupClient}
                >
                  {awsSetupPreparing ? 'Creating...' : 'Create AWS Setup Link'}
                </Button>
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 6, background: `${C.grn}10`, border: `1px solid ${C.grn}33`, color: C.grn, fontSize: 10, fontFamily: FONTS.mono }}>
                  AWS setup link is ready. Launch or copy it for the AWS admin, then verify after CloudFormation completes.
                </div>
                <AwsOnboardingPanel
                  clientId={awsSetupClientId}
                  compact
                  embedded
                  consentStyle
                  onLoaded={data => {
                    if (data?.readiness?.scanReady) setAwsSetupReady(true);
                    if (data?.current?.accountId) setAwsAccountId(data.current.accountId);
                    if (data?.current?.roleArn) setAwsRoleArn(data.current.roleArn);
                    if (data?.current?.partition) setAwsPartition(data.current.partition);
                    if (Array.isArray(data?.current?.regions)) setAwsRegions(data.current.regions.join('\n'));
                  }}
                  onSaved={saved => {
                    if (saved?.accountId) setAwsAccountId(saved.accountId);
                    if (saved?.roleArn) setAwsRoleArn(saved.roleArn);
                    if (saved?.partition) setAwsPartition(saved.partition);
                    if (Array.isArray(saved?.regions)) setAwsRegions(saved.regions.join('\n'));
                    setAwsTestResult({ ok: true, msg: 'AWS setup saved. Verify access before finishing onboarding.' });
                  }}
                  onValidationResult={result => {
                    const ok = Boolean(result?.scanReady || result?.valid);
                    setAwsSetupReady(ok);
                    setAwsTestResult({
                      ok,
                      msg: ok
                        ? 'AWS access verified and scan-ready.'
                        : result?.authenticated
                          ? result?.error || 'AWS authenticated, but scanner permissions are incomplete.'
                          : result?.error || 'AWS verification failed.',
                    });
                  }}
                />
              </div>
            )}
            <details style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8, background: C.s2, border: `1px solid ${C.b1}` }}>
              <summary style={{ cursor: 'pointer', color: C.t2, fontSize: 10, fontFamily: FONTS.mono, fontWeight: 800 }}>
                Advanced AWS options
              </summary>
              <div style={{ marginTop: 10 }}>
                {[
                  { l: 'Account ID', v: awsAccountId, s: setAwsAccountId, p: 'Your AWS Account ID' },
                  { l: 'Region Set', v: awsRegions, s: setAwsRegions, p: 'us-east-1\nus-east-2\nus-gov-west-1' },
                  { l: 'Role ARN', v: awsRoleArn, s: setAwsRoleArn, p: 'Optional if the CloudFormation stack already exists' },
                ].map(f => (
                  <div key={f.l} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>{f.l}</div>
                    {f.l === 'Region Set' ? (
                      <textarea value={f.v} onChange={e => f.s(e.target.value)} placeholder={f.p} rows={3} style={{ ...iS, resize: 'vertical' }} />
                    ) : (
                      <input type={f.t || 'text'} autoComplete={f.t === 'password' ? 'new-password' : undefined} value={f.v} onChange={e => f.s(e.target.value)} placeholder={f.p} style={iS} />
                    )}
                  </div>
                ))}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Partition</div>
                  <select value={awsPartition} onChange={e => setAwsPartition(e.target.value)} style={iS}>
                    <option value="aws">Commercial</option>
                    <option value="aws-us-gov">GovCloud</option>
                  </select>
                </div>
                <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 8, background: `${C.amb}08`, border: `1px solid ${C.amb}22` }}>
                  <div style={{ fontSize: 10, color: C.amb, fontWeight: 800, fontFamily: FONTS.mono, marginBottom: 8 }}>Access-key fallback</div>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.5, marginBottom: 10 }}>
                    Use only when role-based setup cannot be completed. The AWS role remains the preferred scanner path.
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Access Key ID</div>
                      <input value={awsAccessKey} onChange={e => setAwsAccessKey(e.target.value)} placeholder="Fallback IAM access key" style={iS} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>Secret Access Key</div>
                      <input type="password" autoComplete="new-password" value={awsSecretKey} onChange={e => setAwsSecretKey(e.target.value)} placeholder="Fallback IAM secret key" style={iS} />
                    </div>
                  </div>
                </div>
                <Button variant="sec" size="sm" disabled={awsTesting} onClick={testAws}>
                  {awsTesting ? 'Checking...' : awsSetupReady ? 'AWS Scan-Ready' : 'Check AWS Inputs'}
                </Button>
              </div>
            </details>
            {awsTestResult && (
              <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: awsTestResult.ok ? `${C.grn}12` : C.redBg, border: `1px solid ${awsTestResult.ok ? C.grn : C.red}33`, color: awsTestResult.ok ? C.grn : C.red }}>
                {awsTestResult.msg}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="sec" onClick={() => setStep(3)}>{'\u2190'} Back</Button>
        <Button variant="pri" onClick={() => setStep(5)}>Next {'\u2192'}</Button>
      </div>
    </Card>
  );

  const renderStep5 = () => (
    <Card style={{ padding: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1, marginBottom: 6 }}>Scan Scope</div>
      <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 18 }}>
        Capture the operational details needed for DAST, AD, sensitive data discovery, and remote host scanning. Nessus scanner IDs and scan IDs are intentionally skipped for now.
      </div>

      <div style={{ marginBottom: 16, padding: 16, borderRadius: 8, border: `1px solid ${C.b1}`, background: C.s1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 12 }}>DAST</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>WEB TARGETS</div>
            <textarea value={dastWebTargets} onChange={e => setDastWebTargets(e.target.value)} placeholder={"https://portal.example.com\nhttps://api.example.com"} rows={5} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>AUTH REQUIREMENTS</div>
            <textarea value={dastAuthRequirements} onChange={e => setDastAuthRequirements(e.target.value)} placeholder={"Unauthenticated baseline\nTest user required for authenticated scan\nMFA bypass/test token owner"} rows={5} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>RUNNER LOCATION</div>
            <input value={dastRunnerLocation} onChange={e => setDastRunnerLocation(e.target.value)} placeholder="Win11 scanner host, WireGuard .2, Azure runner, etc." style={iS} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>ALLOWED SCAN WINDOWS</div>
            <textarea value={dastAllowedScanWindows} onChange={e => setDastAllowedScanWindows(e.target.value)} placeholder={"Fri 20:00-23:00 CT\nSat 09:00-12:00 CT"} rows={3} style={{ ...iS, resize: 'vertical' }} />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16, padding: 16, borderRadius: 8, border: `1px solid ${C.b1}`, background: C.s1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 12 }}>Active Directory</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>DOMAIN FQDN</div>
            <input value={adDomainFqdn} onChange={e => setAdDomainFqdn(e.target.value)} placeholder="corp.example.local" style={iS} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>LDAPS / CONNECTIVITY STATUS</div>
            <select value={adLdapsStatus} onChange={e => setAdLdapsStatus(e.target.value)} style={iS}>
              <option value="unknown">Unknown</option>
              <option value="confirmed">Confirmed</option>
              <option value="blocked">Blocked / Needs Firewall</option>
              <option value="not-required">Not Required Yet</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>DC HOST / IP LIST</div>
            <textarea value={adDcHostsIps} onChange={e => setAdDcHostsIps(e.target.value)} placeholder={"DC01 10.60.0.10\nDC02 10.60.0.11"} rows={4} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>REQUIRED RIGHTS</div>
            <textarea value={adRequiredRights} onChange={e => setAdRequiredRights(e.target.value)} rows={4} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>SERVICE ACCOUNT UPN</div>
            <input value={adServiceAccountUpn} onChange={e => setAdServiceAccountUpn(e.target.value)} placeholder="svc-sentinel-scan@corp.example.local" style={iS} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>SERVICE ACCOUNT PASSWORD</div>
            <input type="password" autoComplete="new-password" value={adServiceAccountPassword} onChange={e => setAdServiceAccountPassword(e.target.value)} placeholder="Stored as client secret" style={iS} />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16, padding: 16, borderRadius: 8, border: `1px solid ${C.b1}`, background: C.s1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 12 }}>Sensitive Data Discovery</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {SDD_SOURCES.map(src => {
            const checked = sddEnabledSources.includes(src.id);
            return (
              <label key={src.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 6, border: `1px solid ${checked ? C.sky : C.b1}`, background: checked ? `${C.sky}10` : C.s2, fontSize: 10, color: checked ? C.sky : C.t2, fontFamily: FONTS.mono, cursor: 'pointer' }}>
                <input type="checkbox" checked={checked} onChange={() => toggleSddSource(src.id)} />
                {src.name}
              </label>
            );
          })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>S3 BUCKET LIST</div>
            <textarea value={sddS3Buckets} onChange={e => setSddS3Buckets(e.target.value)} placeholder={"client-data-prod\nclient-archive-2026"} rows={4} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>FILE SHARE PATHS</div>
            <textarea value={sddFileSharePaths} onChange={e => setSddFileSharePaths(e.target.value)} placeholder={"\\\\FS01\\Finance\n\\\\FS01\\Clients"} rows={4} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>SHAREPOINT SCOPE</div>
            <textarea value={sddSharePointScopes} onChange={e => setSddSharePointScopes(e.target.value)} placeholder={"Tenant-wide\n/sites/Finance\n/sites/Clients"} rows={4} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>AZURE BLOB SCOPE</div>
            <textarea value={sddAzureBlobScopes} onChange={e => setSddAzureBlobScopes(e.target.value)} placeholder={"storageacct/container\n/subscriptions/.../resourceGroups/..."} rows={4} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div style={{ gridColumn: '1 / span 2' }}>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>AZURE SQL CONNECTION LIST</div>
            <textarea value={sddAzureSqlConnections} onChange={e => setSddAzureSqlConnections(e.target.value)} placeholder={"Server=tcp:sql.example.database.windows.net;Database=AppDb;Authentication=Active Directory Default\nOr one approved connection descriptor per line"} rows={4} style={{ ...iS, resize: 'vertical' }} />
            <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, marginTop: 3 }}>Stored as a client secret. Do not paste production passwords unless approved for scanner use.</div>
          </div>
          <div style={{ gridColumn: '1 / span 2' }}>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>DECLARED SENSITIVE LOCATIONS</div>
            <textarea
              value={sddDeclaredSensitiveLocations}
              onChange={e => setSddDeclaredSensitiveLocations(e.target.value)}
              placeholder={"Finance Share | file-share | \\\\FS01\\Finance | critical | financial\nHR SharePoint | sharepoint | /sites/HR | high | employee-pii\ns3://client-data-prod/contracts"}
              rows={5}
              style={{ ...iS, resize: 'vertical' }}
            />
            <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, marginTop: 3 }}>Format: Label | source | path | criticality | category. Plain paths are also accepted.</div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 18, padding: 16, borderRadius: 8, border: `1px solid ${C.b1}`, background: C.s1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 12 }}>Remote Hosts</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>HOST LIST</div>
            <textarea value={remoteHostList} onChange={e => setRemoteHostList(e.target.value)} placeholder={"FS01 10.60.12.20\nSQL01 10.60.20.15"} rows={5} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>MAINTENANCE WINDOWS</div>
            <textarea value={remoteHostMaintenanceWindows} onChange={e => setRemoteHostMaintenanceWindows(e.target.value)} placeholder={"Mon-Fri 20:00-23:00 CT\nEmergency read-only checks allowed with approval"} rows={5} style={{ ...iS, resize: 'vertical' }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>PROTOCOL</div>
            <select value={remoteHostProtocol} onChange={e => setRemoteHostProtocol(e.target.value)} style={iS}>
              <option value="WinRM">WinRM</option>
              <option value="SSH">SSH</option>
              <option value="SMB">SMB</option>
              <option value="Mixed">Mixed</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>PORTS</div>
            <input value={remoteHostPorts} onChange={e => setRemoteHostPorts(e.target.value)} placeholder="5986, 22, 445" style={iS} />
          </div>
          <div style={{ gridColumn: '1 / span 2' }}>
            <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginBottom: 4 }}>CREDENTIAL SOURCE</div>
            <input value={remoteHostCredentialSource} onChange={e => setRemoteHostCredentialSource(e.target.value)} placeholder="AD service account, local sentinel-audit, RMM vaulted credential, SSH key in vault" style={iS} />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[
          `${countLabel(dastTargets.length, 'DAST target')}`,
          `${countLabel(adDcList.length, 'DC')}`,
          `${countLabel(sddEnabledSources.length, 'SDD source')}`,
          `${countLabel(sddDeclaredSensitiveLocationList.length, 'sensitive location')}`,
          `${countLabel(remoteHosts.length, 'remote host')}`,
        ].map(item => (
          <span key={item} style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, padding: '5px 8px', borderRadius: 999, border: `1px solid ${C.b1}`, background: C.s2 }}>
            {item}
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="sec" onClick={() => setStep(4)}>{'\u2190'} Back</Button>
        <Button variant="pri" onClick={initModulesForTier}>Next {'\u2192'}</Button>
      </div>
    </Card>
  );

  const renderStep6 = () => (
    <Card style={{ padding: 24 }}>
      <div style={{ fontSize: 15, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1, marginBottom: 6 }}>Module Selection</div>
      <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 18 }}>Choose which scanning modules to enable. Modules above your tier are locked.</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 18 }}>
        {MODULES.map(m => {
          const locked = m.tier > tier;
          const checked = selectedModules.includes(m.id);
          return (
            <div key={m.id} onClick={() => !locked && toggleModule(m.id)} style={{
              padding: '12px 14px', borderRadius: 8,
              cursor: locked ? 'not-allowed' : 'pointer',
              opacity: locked ? 0.4 : 1,
              background: checked ? `${C.sky}10` : C.s2,
              border: `1px solid ${checked ? C.sky : C.b1}`,
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 2,
                background: checked ? C.sky : 'transparent',
                border: `1.5px solid ${checked && !locked ? C.sky : C.t3}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 10, fontWeight: 900,
              }}>{checked ? '\u2713' : ''}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: checked && !locked ? C.sky : C.t1, fontFamily: FONTS.sans }}>{m.name}</div>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 3 }}>{m.desc}</div>
                {locked && <div style={{ fontSize: 8, color: C.amb, fontFamily: FONTS.mono, marginTop: 3 }}>Requires {TIERS[m.tier]?.name} tier or higher</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="sec" onClick={() => setStep(5)}>{'\u2190'} Back</Button>
        <Button variant="pri" disabled={selectedModules.length === 0} onClick={() => setStep(7)}>Next {'\u2192'}</Button>
      </div>
    </Card>
  );

  const renderStep7 = () => (
    <Card style={{ padding: 24 }}>
      {launchSuccess ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: C.grn }}><CheckCircle2 size={32} strokeWidth={1.5} /></div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.grn, fontFamily: FONTS.sans, marginBottom: 8 }}>Client Onboarded Successfully</div>
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 6 }}>
            {name} has been created{scanStatus ? ` and the first scan is ${scanStatus}` : '.'}.
          </div>
          {scanStatus && (
            <div style={{ fontSize: 10, color: C.sky, fontFamily: FONTS.mono, marginBottom: 20 }}>
              Scan status: {scanStatus}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Button variant="pri" onClick={() => navigate('/msp/clients')}>View Clients</Button>
            <Button variant="sec" onClick={() => { if (newClientId) { setClientId(newClientId); setClientName(name); } navigate('/client/dashboard'); }}>Client Dashboard</Button>
            <Button variant="sec" onClick={() => { if (newClientId) { setClientId(newClientId); setClientName(name); } navigate('/client/credentials'); }}>Verify Cloud Access</Button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1, marginBottom: 18 }}>Review & Confirm</div>

          {[
            { l: 'Client Name', v: name },
            { l: 'Primary Domain', v: primaryDomain || '(not set)' },
            { l: 'Tier', v: (Array.isArray(TIERS) ? TIERS : []).find(t => t.id === tier)?.name || String(tier) },
            { l: 'Compliance', v: obligations.length > 0
                ? OBLIGATIONS.filter(o => obligations.includes(o.id)).map(o => o.name).join(', ')
                : '(none)' },
            { l: 'License Key', v: licenseKey || '(auto-generated)' },
            { l: 'OSINT Scope', v: `${countLabel(osintSeedDomains.length, 'domain')}, ${countLabel(osintSeedEmails.length, 'email')}, ${countLabel(osintPublicIpRanges.length, 'IP range')}, ${countLabel(osintKnownAssets.length, 'asset')}` },
            { l: 'Azure / M365', v: azSkipped ? 'Skipped' : azureTenantIds.length > 0 ? `${countLabel(azureTenantIds.length, 'tenant')} / consent ${azConsentConfirmed ? 'confirmed' : 'pending'} / subscription discovery ${azureSubscriptionScopes.length > 0 ? 'allow-list' : 'auto'}` : 'Not configured' },
            { l: 'AWS', v: awsSkipped ? 'Skipped' : awsRoleArn ? `Role outputs captured${awsAccountId ? ` / account ${awsAccountId}` : ''}${awsSetupReady ? ' / scan-ready' : ''}` : awsAccessKey ? `Access-key fallback${awsAccountId ? ` / account ${awsAccountId}` : ''}` : awsSetupClientId ? 'Setup link prepared' : 'Setup link not prepared' },
            { l: 'DAST', v: hasDastScope ? `${countLabel(dastTargets.length, 'target')} / runner ${dastRunnerLocation || 'not set'} / ${countLabel(dastWindows.length, 'window')}` : 'Not configured' },
            { l: 'Active Directory', v: hasAdScope ? `${adDomainFqdn || 'Domain not set'} / ${countLabel(adDcList.length, 'DC')} / LDAPS ${adLdapsStatus}` : 'Not configured' },
            { l: 'Sensitive Data Discovery', v: hasSddScope ? `${countLabel(sddEnabledSources.length, 'source')} / S3 ${sddS3BucketList.length} / shares ${sddFileSharePathList.length} / SP ${sddSharePointScopeList.length} / Blob ${sddAzureBlobScopeList.length} / SQL ${sddAzureSqlConnectionList.length} / sensitive ${sddDeclaredSensitiveLocationList.length}` : 'Not configured' },
            { l: 'Remote Hosts', v: hasRemoteHostScope ? `${countLabel(remoteHosts.length, 'host')} / ${remoteHostProtocol} / ${countLabel(remotePorts.length, 'port')} / ${countLabel(remoteWindows.length, 'window')}` : 'Not configured' },
            { l: 'Modules', v: selectedModules.length > 0
                ? MODULES.filter(m => selectedModules.includes(m.id)).map(m => m.name).join(', ')
                : '(none)' },
          ].map(r => (
            <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.b2}` }}>
              <span style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, flexShrink: 0 }}>{r.l}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, fontFamily: FONTS.mono, textAlign: 'right', marginLeft: 12 }}>{r.v}</span>
            </div>
          ))}

          {launchError && (
            <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{launchError}</span>
            </div>
          )}

          {(launching || completedSteps.length > 0) && (
            <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 6, background: `${C.sky}08`, border: `1px solid ${C.sky}22` }}>
              {completedSteps.map(s => (
                <div key={s} style={{ fontSize: 11, fontFamily: FONTS.mono, color: C.grn, padding: '3px 0' }}>
                  {'\u2713'} {s}
                </div>
              ))}
              {stepWarnings.map(w => (
                <div key={w.label} style={{ fontSize: 11, fontFamily: FONTS.mono, color: C.amb, padding: '3px 0' }}>
                  {'\u26A0'} {w.label}: {w.msg}
                </div>
              ))}
              {launching && (
                <div style={{ fontSize: 11, color: C.sky, fontFamily: FONTS.mono, padding: '3px 0' }}>
                  {'\u2192'} {launchStep}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <Button variant="sec" onClick={() => setStep(6)} disabled={launching}>{'\u2190'} Back</Button>
            <Button variant="pri" onClick={handleLaunch} disabled={launching}>
              {launching ? 'Creating...' : 'Confirm & Launch Scan'}
            </Button>
          </div>
        </>
      )}
    </Card>
  );

  return (
    <Shell title="Onboard New Client" sub={`Step ${step + 1} of ${STEPS.length} \u2014 ${STEPS[step]}`}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <StepBar step={step} />
        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
        {step === 6 && renderStep6()}
        {step === 7 && renderStep7()}
      </div>
    </Shell>
  );
}
